// supervisor-loop-post-route-finalize.ts
//
// Split out of supervisor-loop.ts (bước 0b, phase 03 — trần dòng B20): the
// post-route half of one control-loop tick — promote a review report,
// persist retry/backoff state, journal attempt_finished, and record cost
// accounting. Pure move from runOneSupervisorTick's tail; the only textual
// change vs. the original block is `input.` -> `tickInput.` (renamed param).
import { extractWorkerDecisionsFromResult } from "./attempt-finished-worker-decisions.js";
import { isContractErrorAttempt } from "./contract-error-circuit-breaker.js";
import { recordAttemptUsage } from "./mission-cost-accounting.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import type { RetryState } from "./retry-policy.js";
import { promoteReviewReportAfterDoneRoute } from "./review-report-promotion-wiring.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import type { AttemptStateFile } from "./supervisor-attempt-state.js";
import {
  formatRawResultTailForJournal,
  formatSchemaErrorDetail,
} from "./supervisor-forensics-archival.js";
import { journalAttemptOutcome } from "./supervisor-loop-outcome-journal.js";
import { persistPostRouteAttemptState } from "./supervisor-loop-post-route-attempt-state.js";
import type { SupervisorTickInput } from "./supervisor-loop-types.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";
import { extractReportedGapsFromResult } from "./ticket-reported-gaps.js";

export interface FinalizeAttemptInput {
  tickInput: SupervisorTickInput;
  ticket: Ticket;
  cycle: AttemptCycleResult;
  route: RouteOutcomeResult;
  attemptState: AttemptStateFile;
  attemptNo: number;
  priorRetryState: RetryState;
  priorBackoffIndex: number;
  /** Phase 03 (R-15, Req 2): wall-clock at journalAttemptStart — the ■ line's mm:ss is `now() − attemptStartedAt`. */
  attemptStartedAt: Date;
}

export async function finalizeAttemptAfterRoute(input: FinalizeAttemptInput): Promise<void> {
  const { tickInput, ticket, cycle, route, attemptState, attemptNo, priorRetryState } = input;

  // P20 ("0 caller" #12): carry a review-local-v1 worker's report from its
  // own attempt staging dir into `<controlRoot>/review-staging/<sha>.json`,
  // the ONLY place the release flow reads it from. Runs after routing so the
  // SHA used is the commit the attempt actually produced — the same SHA
  // `git rev-parse HEAD` yields when the release flow looks the report up.
  // P21: `store` makes the outcome observable — a promotion that ran and a
  // promotion that found nothing used to look identical from outside.
  await promoteReviewReportAfterDoneRoute({
    controlRoot: tickInput.controlRoot,
    repoRoot: tickInput.repoDir,
    ticket,
    cycle,
    route,
    store: tickInput.store,
  });

  await persistPostRouteAttemptState({
    controlRoot: tickInput.controlRoot,
    attemptState,
    ticketId: ticket.id,
    attemptNo,
    priorRetryState,
    priorBackoffIndex: input.priorBackoffIndex,
    route,
  });

  // Schema-error detail (P13 Agent A) + terminal-status/verdict journaling
  // extracted to supervisor-loop-outcome-journal.ts (split, review
  // 260909-p6-m2 INFO) — same behavior, moved verbatim.
  const schemaErrorDetail =
    cycle.kind === "verified"
      ? formatSchemaErrorDetail(cycle.sessionResult.schemaErrorPaths)
      : undefined;
  // Instrumentation 2026-09-15 (dogfood phần 3): the raw result tail used to
  // die with the process for no_result_json_found/invalid_structured_output_
  // schema — session-runner only populates it on a session-level
  // contract_error, and the explicit outcome check keeps that contract
  // visible here rather than relying on the field's own absence.
  // Codex F9: known-values HIỆN TẠI (test.env có thể xuất hiện/đổi sau spawn)
  // — mọi text worker dưới đây che bằng chúng TRƯỚC khi cắt.
  const knownValues =
    cycle.kind === "verified" ? await knownValuesForControlRoot(tickInput.store.controlRoot) : [];
  const rawResultTail =
    cycle.kind === "verified" && cycle.sessionResult.outcome === "contract_error"
      ? formatRawResultTailForJournal(cycle.sessionResult.rawResultTail, knownValues)
      : undefined;
  // A2: the worker's declared coverage limit rides on the done
  // attempt_finished so the release flow can put it in the MR description.
  const reportedGaps =
    cycle.kind === "verified"
      ? extractReportedGapsFromResult(cycle.sessionResult.result)
      : undefined;
  // V2-1 E13: the worker's own decision notes, on this attempt (only when non-empty).
  const workerDecisionFields =
    cycle.kind === "verified"
      ? extractWorkerDecisionsFromResult(cycle.sessionResult.result, knownValues)
      : undefined;
  // 06g R5: the ■ line and the usage record read the same `usage` object.
  const usage = cycle.kind === "verified" ? cycle.sessionResult.usage : undefined;
  await journalAttemptOutcome({
    store: tickInput.store,
    ticketId: ticket.id,
    attemptNo,
    route,
    schemaErrorDetail,
    rawResultTail,
    reportedGaps,
    workerDecisionFields,
    progress: tickInput.progress,
    now: tickInput.now,
    attemptStartedAt: input.attemptStartedAt,
    attemptCostUsd: usage?.costUsd,
  });

  // Phase 04 (R-23, D11): recorded AFTER journalAttemptOutcome (same ordering
  // rule as the ■ line — journal write is the source of truth) so the
  // breaker's ticket list always matches what was actually journaled. This is
  // the ONE call site shared by both the feature-ticket dispatch path and the
  // review-candidate path (same reason journalAttemptOutcome is).
  //
  // Sau review M1: route.kind === "done" là tín hiệu "attempt thành công"
  // DUY NHẤT, chung cho cả feature ticket lẫn review candidate (route không
  // phân biệt nguồn — supervisor-outcome-router.ts's isDoneOutcome). 1 done
  // reset breaker TRƯỚC KHI record attempt hiện tại (dù logic 2 nhánh loại
  // trừ nhau — done không thể đồng thời là contract_error — thứ tự reset
  // trước vẫn đúng ngữ nghĩa "done xoá lịch sử lỗi cũ").
  // 06h R3: an `interrupted` route (operator signal, no result) is decided by
  // the ROUTE alone — the flag is not read a second time here, so router and
  // breaker can never disagree. `isContractErrorAttempt` itself is unchanged.
  const interrupted = route.kind === "retry_decision" && route.decision.category === "interrupted";
  if (route.kind === "done") {
    tickInput.contractErrorBreaker?.reset();
  } else if (!interrupted && isContractErrorAttempt(cycle, route)) {
    tickInput.contractErrorBreaker?.record(ticket.id);
  }

  // P13 Agent B: cost/attempt accounting is only real once the production
  // attempt-finish path records it — the guard in supervisor-dispatch-guards
  // reads these events back. Usage is undefined when the worker never
  // reported one (no spawn, or a provider without `usage`); the attempt is
  // still counted so `max_attempts_per_mission` can never be evaded.
  // 06h R5b (F-42, B4): an interrupted attempt with no cost reported (worker
  // killed before its result event) gets NO usage row — `attempts_without_
  // usage` then counts it honestly instead of a fabricated $0.
  if (interrupted && !(usage?.costUsd !== undefined && usage.costUsd > 0)) return;
  await recordAttemptUsage(tickInput.store, {
    ticket: ticket.id,
    attemptNo,
    inputTokens: usage?.inputTokens,
    outputTokens: usage?.outputTokens,
    costUsd: usage?.costUsd,
  });
}
